'use client';

import { ShieldCheck } from 'lucide-react';
import { useRef } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';
import { cn } from '@/libs/utils';
import { useResponsiveSheetSide } from '@/presentation/hooks/useResponsiveSheetSide';
import type { Pay4MeEntrySource } from '@/presentation/stores/ModalStore';

interface Pay4MeDrawerProps {
  open: boolean;
  source: Pay4MeEntrySource;
  productId?: string;
  returnFocusElement?: HTMLElement | null;
  onOpenChange: (open: boolean) => void;
  onFocusRestored?: () => void;
}

const HOW_IT_WORKS = [
  {
    number: 1,
    title: 'Start a plan',
    body: 'Choose your item and begin paying small small.',
    badgeClassName: 'bg-[#F4F5F7] text-[#0C0C0C]',
  },
  {
    number: 2,
    title: 'Share your link',
    body: 'Send your Pay4Me link to family and friends.',
    badgeClassName: 'bg-[#E5FAEF] text-[#008750]',
  },
  {
    number: 3,
    title: 'Get your item sooner',
    body: 'Their contributions reduce what you have left to pay.',
    badgeClassName: 'bg-[#E5F8FC] text-[#007D91]',
  },
] as const;

function ContributionExample() {
  return (
    <section className="mt-6 rounded-[18px] bg-[#111111] p-4 text-white">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[16px] font-extrabold leading-tight">
          See how a little help adds up
        </h3>
        <span className="shrink-0 rounded-full bg-[#2B2B2B] px-2.5 py-1 text-[10px] font-semibold text-[#D5D7DD]">
          Example
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-sm font-bold text-[#344054]">
            A
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">Ama</p>
            <p className="text-xs text-[#B4B7C0]">Family</p>
          </div>
          <p className="shrink-0 text-sm font-bold text-[#77EFFE]">+ GHS 200</p>
        </div>

        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-sm font-bold text-[#344054]">
            K
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">Kofi</p>
            <p className="text-xs text-[#B4B7C0]">Friend</p>
          </div>
          <p className="shrink-0 text-sm font-bold text-[#77EFFE]">+ GHS 150</p>
        </div>
      </div>

      <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-[#4A4A4A]" aria-hidden="true">
        <div className="flex h-full w-[76%]">
          <span className="h-full w-[55%] bg-[#85E9AD]" />
          <span className="h-full flex-1 bg-[#77EFFE]" />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-medium text-[#D1D5DB]">
        <span className="inline-flex items-center gap-1">
          <span className="size-1.5 rounded-full bg-[#85E9AD]" aria-hidden="true" />
          You
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="size-1.5 rounded-full bg-[#77EFFE]" aria-hidden="true" />
          Family &amp; friends
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="size-1.5 rounded-full bg-[#4A4A4A]" aria-hidden="true" />
          Left
        </span>
      </div>
    </section>
  );
}

function Pay4MeDrawer({
  open,
  source,
  productId,
  returnFocusElement,
  onOpenChange,
  onFocusRestored,
}: Pay4MeDrawerProps) {
  const side = useResponsiveSheetSide();
  const isHomepage = source === 'homepage';
  const ctaLabel = isHomepage ? 'Browse products' : 'Start a Plan';
  const ctaHref = isHomepage ? '#marketplace-products' : '#pdp-checkout-box';
  const ctaDestinationId = isHomepage ? 'marketplace-products' : 'pdp-checkout-box';
  const focusDestinationOnCloseRef = useRef(false);

  const handleCtaClick = () => {
    focusDestinationOnCloseRef.current = true;
    trackConsumerEvent({
      eventType: 'pay4me_intent_clicked',
      properties: {
        entry_point: isHomepage ? 'homepage_showcase' : 'pdp_checkout_box',
        destination: isHomepage ? 'browse_products' : 'pdp_checkout_box',
        source: 'pay4me_education_drawer',
        source_data: productId
          ? `pay4me_pdp_banner:${productId}`
          : 'pay4me_showcase_card',
      },
    });
    onOpenChange(false);
  };

  const handleCloseAutoFocus = (event: Event) => {
    if (focusDestinationOnCloseRef.current) {
      event.preventDefault();
      document.getElementById(ctaDestinationId)?.focus({ preventScroll: true });
      focusDestinationOnCloseRef.current = false;
      onFocusRestored?.();
      return;
    }

    if (returnFocusElement?.isConnected) {
      event.preventDefault();
      returnFocusElement.focus();
    }
    onFocusRestored?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        onCloseAutoFocus={handleCloseAutoFocus}
        className={cn(
          'flex w-full flex-col gap-0 overflow-hidden border-0 bg-white p-0',
          '[&>button]:right-4 [&>button]:top-4 [&>button]:flex [&>button]:size-11 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-[#F4F5F7] [&>button]:opacity-100 [&>button]:focus:!ring-0 [&>button]:focus:!ring-offset-0 [&>button]:focus-visible:!ring-2 [&>button]:focus-visible:!ring-motitoBlue [&>button]:focus-visible:!ring-offset-2',
          '[&>button>svg]:size-5 [&>button>svg]:text-[#344054]',
          side === 'right' && 'sm:max-w-[430px]',
          side === 'bottom' && 'max-h-[94dvh] rounded-t-[28px]'
        )}
      >
        {side === 'bottom' ? (
          <div className="flex h-5 shrink-0 items-center justify-center" aria-hidden="true">
            <span className="h-1 w-11 rounded-full bg-[#D0D3D9]" />
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-8 sm:px-6 sm:pt-16">
          <div className="flex flex-col gap-4 text-center">
            <SheetTitle className="text-[26px] font-extrabold leading-[1.12] tracking-[-0.02em] text-[#111111]">
              Finish your plan faster
            </SheetTitle>
            <SheetDescription className="mx-auto max-w-[34ch] text-[15px] leading-6 text-[#667085]">
              Share a Pay4Me link. Family and friends can contribute any amount toward your plan.
            </SheetDescription>
          </div>

          <ContributionExample />

          <ol className="mt-6 space-y-4">
            {HOW_IT_WORKS.map((step) => (
              <li key={step.number} className="flex gap-3.5">
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full text-base font-bold',
                    step.badgeClassName
                  )}
                >
                  {step.number}
                </span>
                <div className="pt-0.5">
                  <p className="text-[16px] font-extrabold leading-tight text-[#111111]">
                    {step.title}
                  </p>
                  <p className="mt-1 text-[14px] leading-5 text-[#747474]">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-6 flex items-center gap-3 rounded-[16px] bg-[#E9EEFD] px-4 py-4 text-[#111111]">
            <ShieldCheck className="size-6 shrink-0 text-[#111111]" aria-hidden="true" />
            <p className="text-[14px] font-bold leading-5">
              Contributions go directly toward your plan.
            </p>
          </div>
        </div>

        <div className="shrink-0 border-t border-[#EAECF0] bg-white px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-4 sm:px-6">
          <a
            href={ctaHref}
            onClick={handleCtaClick}
            className="flex min-h-14 w-full items-center justify-center rounded-full bg-[#111111] px-6 text-center text-[16px] font-extrabold text-white transition-colors hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-motitoBlue focus-visible:ring-offset-2"
          >
            {ctaLabel}
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}

Pay4MeDrawer.displayName = 'Pay4MeDrawer';

export default Pay4MeDrawer;
